'use client'

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react'
import { cx } from './utils/cx'

export function List({
  children,
  className,
  style
}: {
  children?: ReactNode
  className?: string
  style?: CSSProperties
}) {
  return (
    <div className={cx('st-list', className)} style={style}>
      {children}
    </div>
  )
}

interface ListRowProps {
  open?: boolean
  onToggle?: () => void
  head?: ReactNode
  body?: ReactNode
  children?: ReactNode
  className?: string
  style?: CSSProperties
}

export function ListRow({ open, onToggle, head, body, children, className, style }: ListRowProps) {
  const isCompound = children != null
  const interactive = onToggle != null
  return (
    <article className={cx('st-row', open && 'open', className)} style={style}>
      {isCompound ? (
        children
      ) : (
        <>
          <div className="st-row-head" onClick={onToggle} style={interactive ? undefined : { cursor: 'default' }}>
            {head}
            {interactive && (
              <button
                type="button"
                className="st-caret"
                aria-label="Toggle details"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle?.()
                }}
              />
            )}
          </div>
          {body && <div className="st-row-body">{body}</div>}
        </>
      )}
    </article>
  )
}

export function ListRowHead({
  children,
  onToggle,
  className,
  toggleAriaLabel = 'Toggle details'
}: {
  children?: ReactNode
  onToggle?: MouseEventHandler
  className?: string
  toggleAriaLabel?: string
}) {
  const interactive = onToggle != null
  // The outer container intentionally avoids role="button" so that the inner
  // ListRowActions buttons aren't nested inside a button in the a11y tree.
  // Mouse users still get click-anywhere-to-toggle via onClick on the div;
  // keyboard / AT users use the explicit caret button below.
  return (
    <div
      className={cx('st-row-head', className)}
      onClick={onToggle}
      style={interactive ? undefined : { cursor: 'default' }}>
      {children}
      {interactive && (
        <button
          type="button"
          className="st-caret"
          aria-label={toggleAriaLabel}
          onClick={(e) => {
            // The div's onClick will also fire and trigger onToggle. Prevent
            // double-toggling by stopping propagation here.
            e.stopPropagation()
            onToggle?.(e)
          }}
        />
      )}
    </div>
  )
}

export function ListRowBody({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cx('st-row-body', className)}>{children}</div>
}

export function ListRowTitle({ title, sub, className }: { title?: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className={cx('st-row-title', className)}>
      {title}
      {sub && <span className="sub">{sub}</span>}
    </div>
  )
}

export function ListRowActions({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <div className={cx('st-row-actions', className)} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  )
}
